import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Step {
  id: string;
  phase: string;
  title: string;
  location: string;
  duration: string;
  details: string[];
  code?: string;
  status: 'pending' | 'active' | 'completed' | 'error';
}

interface Process {
  name: string;
  description: string;
  steps: Step[];
}

const BusinessFileSharingSteps: React.FC = () => {
  const [activeProcess, setActiveProcess] = useState<'create' | 'upload' | 'list'>('create');
  const [runningSimulation, setRunningSimulation] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [businessName, setBusinessName] = useState('Acme Corporation');
  const [slug, setSlug] = useState('acme-corporation');

  const processes: Record<string, Process> = {
    create: {
      name: 'Create Business Folder & Route',
      description: 'Complete process of setting up a new business file sharing link',
      steps: [
        {
          id: 'validate',
          phase: 'Input Validation',
          title: 'Validate Business Name',
          location: 'Browser (JavaScript)',
          duration: '< 1ms',
          details: [
            'Check name length >= 3 characters',
            'Remove special characters',
            'Convert to URL-safe slug',
            'Check for reserved words'
          ],
          code: `// Browser JavaScript
const slug = businessName
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

if (slug.length < 3) throw new Error('Too short');
if (RESERVED_WORDS.includes(slug)) throw new Error('Reserved');`,
          status: 'pending'
        },
        {
          id: 'check-availability',
          phase: 'Database Check',
          title: 'Check Slug Availability',
          location: 'CloudFlare Worker → D1',
          duration: '20-30ms',
          details: [
            'Worker receives POST /api/business/check',
            'Query D1 database for existing slug',
            'Return availability status'
          ],
          code: `// CloudFlare Worker
const { results } = await env.DB.prepare(
  "SELECT id FROM business_routes WHERE slug = ?"
).bind(slug).all();

if (results.length > 0) {
  return json({ available: false }, 409);
}`,
          status: 'pending'
        },
        {
          id: 'create-route',
          phase: 'Route Creation',
          title: 'Insert Route into D1 Database',
          location: 'CloudFlare D1 (SQLite)',
          duration: '30-50ms',
          details: [
            'Generate unique route ID',
            'Insert business route record',
            'Set creation timestamp',
            'Configure permissions'
          ],
          code: `// D1 Database Insert
await env.DB.prepare(\`
  INSERT INTO business_routes (
    id, business_name, slug,
    created_at, is_active,
    owner_id, settings
  ) VALUES (?, ?, ?, ?, ?, ?, ?)
\`).bind(
  generateId(),
  businessName,
  slug,
  new Date().toISOString(),
  1,
  userId,
  JSON.stringify(settings)
).run();`,
          status: 'pending'
        },
        {
          id: 'create-r2-folder',
          phase: 'Storage Setup',
          title: 'Create R2 Storage Folder',
          location: 'CloudFlare R2',
          duration: '50-100ms',
          details: [
            'Create folder structure in R2',
            'Set folder metadata',
            'Configure access permissions',
            'Create .metadata file'
          ],
          code: `// R2 Folder Creation
const folderKey = \`business/\${slug}/.metadata\`;
await env.R2.put(folderKey, JSON.stringify({
  businessName,
  createdAt: new Date().toISOString(),
  uploadCount: 0,
  totalSize: 0
}), {
  httpMetadata: {
    contentType: 'application/json'
  }
});`,
          status: 'pending'
        },
        {
          id: 'create-index',
          phase: 'Index Creation',
          title: 'Create Search Index Entry',
          location: 'CloudFlare D1',
          duration: '20-30ms',
          details: [
            'Add to search index table',
            'Create full-text search entry',
            'Update route cache'
          ],
          code: `// Search Index
await env.DB.prepare(\`
  INSERT INTO search_index (
    route_id, searchable_text,
    route_type
  ) VALUES (?, ?, 'business')
\`).bind(
  routeId,
  \`\${businessName} \${slug}\`
).run();`,
          status: 'pending'
        },
        {
          id: 'generate-url',
          phase: 'URL Generation',
          title: 'Generate Shareable URL',
          location: 'CloudFlare Worker',
          duration: '< 1ms',
          details: [
            'Construct full URL',
            'Generate short link if enabled',
            'Return to client'
          ],
          code: `// URL Generation
const url = \`https://happyhumantools.com/files/\${slug}\`;
return json({
  success: true,
  url,
  slug,
  routeId
});`,
          status: 'pending'
        }
      ]
    },
    upload: {
      name: 'File Upload Process',
      description: 'What happens when someone uploads a file to a business link',
      steps: [
        {
          id: 'verify-route',
          phase: 'Route Verification',
          title: 'Verify Business Route Exists',
          location: 'CloudFlare Worker → D1',
          duration: '20-30ms',
          details: [
            'Extract slug from URL',
            'Query D1 for route details',
            'Check if route is active',
            'Verify upload permissions'
          ],
          code: `// Route Verification
const route = await env.DB.prepare(\`
  SELECT * FROM business_routes
  WHERE slug = ? AND is_active = 1
\`).bind(slug).first();

if (!route) {
  return error('Route not found', 404);
}`,
          status: 'pending'
        },
        {
          id: 'validate-file',
          phase: 'File Validation',
          title: 'Validate Upload File',
          location: 'CloudFlare Worker',
          duration: '< 5ms',
          details: [
            'Check file size limits',
            'Verify file type allowed',
            'Scan filename for security',
            'Check storage quota'
          ],
          code: `// File Validation
const settings = JSON.parse(route.settings);
if (file.size > settings.maxFileSize) {
  return error('File too large');
}
if (!settings.allowedTypes.includes(file.type)) {
  return error('File type not allowed');
}`,
          status: 'pending'
        },
        {
          id: 'generate-key',
          phase: 'Key Generation',
          title: 'Generate R2 Storage Key',
          location: 'CloudFlare Worker',
          duration: '< 1ms',
          details: [
            'Generate unique file ID',
            'Create storage path',
            'Add timestamp to filename',
            'Prevent naming collisions'
          ],
          code: `// Storage Key Generation
const fileId = crypto.randomUUID();
const timestamp = Date.now();
const storageKey = \`business/\${slug}/\${timestamp}_\${fileId}_\${file.name}\`;`,
          status: 'pending'
        },
        {
          id: 'upload-r2',
          phase: 'File Upload',
          title: 'Upload File to R2 Storage',
          location: 'CloudFlare R2',
          duration: '100-500ms',
          details: [
            'Stream file to R2',
            'Set file metadata',
            'Generate checksums',
            'Confirm upload success'
          ],
          code: `// R2 Upload
await env.R2.put(storageKey, file.stream(), {
  httpMetadata: {
    contentType: file.type,
    contentDisposition: \`attachment; filename="\${file.name}"\`
  },
  customMetadata: {
    uploaderId: uploaderId,
    uploaderEmail: email,
    uploadedAt: new Date().toISOString()
  }
});`,
          status: 'pending'
        },
        {
          id: 'record-upload',
          phase: 'Database Record',
          title: 'Record Upload in D1',
          location: 'CloudFlare D1',
          duration: '30-50ms',
          details: [
            'Insert file record',
            'Link to business route',
            'Store uploader info',
            'Update statistics'
          ],
          code: `// D1 Upload Record
await env.DB.prepare(\`
  INSERT INTO uploads (
    id, route_id, file_name,
    file_size, storage_key,
    uploader_email, uploaded_at
  ) VALUES (?, ?, ?, ?, ?, ?, ?)
\`).bind(
  fileId, route.id, file.name,
  file.size, storageKey,
  email, new Date().toISOString()
).run();

// Update route stats
await env.DB.prepare(\`
  UPDATE business_routes
  SET upload_count = upload_count + 1,
      total_size = total_size + ?
  WHERE id = ?
\`).bind(file.size, route.id).run();`,
          status: 'pending'
        },
        {
          id: 'send-notification',
          phase: 'Notification',
          title: 'Send Upload Notification',
          location: 'CloudFlare Worker → SES',
          duration: '200-300ms',
          details: [
            'Prepare email notification',
            'Send via AWS SES',
            'Log notification sent',
            'Return success to uploader'
          ],
          code: `// Email Notification
if (settings.notifyOnUpload) {
  await sendEmail({
    to: route.owner_email,
    subject: \`New file uploaded to \${businessName}\`,
    body: \`File: \${fileName} (\${(fileSize/1024/1024).toFixed(2)}MB)
From: \${email}
Message: \${message}\`
  });
}`,
          status: 'pending'
        }
      ]
    },
    list: {
      name: 'List Uploaded Files',
      description: 'Process of retrieving and displaying files at a business URL',
      steps: [
        {
          id: 'load-route',
          phase: 'Route Loading',
          title: 'Load Business Route',
          location: 'CloudFlare Worker → D1',
          duration: '20-30ms',
          details: [
            'Extract slug from URL path',
            'Query route from D1',
            'Check access permissions',
            'Load route settings'
          ],
          code: `// Load Route
const route = await env.DB.prepare(\`
  SELECT r.*,
    COUNT(u.id) as file_count,
    SUM(u.file_size) as total_size
  FROM business_routes r
  LEFT JOIN uploads u ON r.id = u.route_id
  WHERE r.slug = ?
  GROUP BY r.id
\`).bind(slug).first();`,
          status: 'pending'
        },
        {
          id: 'query-uploads',
          phase: 'File Query',
          title: 'Query Uploaded Files',
          location: 'CloudFlare D1',
          duration: '30-50ms',
          details: [
            'Query uploads table',
            'Join with file metadata',
            'Apply sorting (newest first)',
            'Paginate results'
          ],
          code: `// Query Uploads
const uploads = await env.DB.prepare(\`
  SELECT * FROM uploads
  WHERE route_id = ?
  ORDER BY uploaded_at DESC
  LIMIT ? OFFSET ?
\`).bind(route.id, limit, offset).all();`,
          status: 'pending'
        },
        {
          id: 'generate-urls',
          phase: 'URL Generation',
          title: 'Generate Download URLs',
          location: 'CloudFlare Worker',
          duration: '10-20ms',
          details: [
            'Create signed URLs for each file',
            'Set URL expiration (1 hour)',
            'Add download tokens',
            'Prepare response data'
          ],
          code: `// Generate Download URLs
const filesWithUrls = uploads.results.map(file => ({
  ...file,
  downloadUrl: generateSignedUrl(file.storage_key, 3600),
  thumbnailUrl: file.type.startsWith('image/')
    ? generateThumbnailUrl(file.storage_key)
    : null
}));`,
          status: 'pending'
        },
        {
          id: 'check-cache',
          phase: 'Cache Check',
          title: 'Check CDN Cache',
          location: 'CloudFlare CDN',
          duration: '< 5ms',
          details: [
            'Check if listing is cached',
            'Validate cache freshness',
            'Return cached data if fresh',
            'Otherwise continue to render'
          ],
          code: `// Cache Headers
const cacheKey = \`listing:\${slug}\`;
const cached = await env.KV.get(cacheKey, 'json');

if (cached && cached.expires > Date.now()) {
  return cached.data;
}`,
          status: 'pending'
        },
        {
          id: 'render-page',
          phase: 'Page Rendering',
          title: 'Render File Gallery',
          location: 'Browser (React)',
          duration: '50-100ms',
          details: [
            'Receive JSON response',
            'Render file gallery UI',
            'Display file thumbnails',
            'Show download buttons'
          ],
          code: `// React Rendering
return (
  <FileGallery>
    {files.map(file => (
      <FileCard
        key={file.id}
        name={file.name}
        size={file.size}
        uploadedAt={file.uploaded_at}
        downloadUrl={file.downloadUrl}
      />
    ))}
  </FileGallery>
);`,
          status: 'pending'
        },
        {
          id: 'update-cache',
          phase: 'Cache Update',
          title: 'Update CDN Cache',
          location: 'CloudFlare KV',
          duration: '20-30ms',
          details: [
            'Store rendered listing in KV',
            'Set cache expiration (5 min)',
            'Purge old cache entries',
            'Log cache update'
          ],
          code: `// Update Cache
await env.KV.put(
  cacheKey,
  JSON.stringify({
    data: filesWithUrls,
    expires: Date.now() + 300000 // 5 minutes
  }),
  { expirationTtl: 300 }
);`,
          status: 'pending'
        }
      ]
    }
  };

  const runSimulation = async (processKey: string) => {
    setRunningSimulation(true);
    setCurrentStepIndex(0);

    const process = processes[processKey];
    const steps = [...process.steps];

    // Reset all steps to pending
    steps.forEach(step => step.status = 'pending');

    // Run through each step
    for (let i = 0; i < steps.length; i++) {
      setCurrentStepIndex(i);
      steps[i].status = 'active';

      // Simulate processing time
      const duration = parseInt(steps[i].duration) || 100;
      await new Promise(resolve => setTimeout(resolve, Math.min(duration * 10, 500)));

      steps[i].status = 'completed';
    }

    setRunningSimulation(false);
    setCurrentStepIndex(-1);

    // Reset after 3 seconds
    setTimeout(() => {
      steps.forEach(step => step.status = 'pending');
      setCurrentStepIndex(-1);
    }, 3000);
  };

  const currentProcess = processes[activeProcess];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Business File Sharing - Technical Steps
          </h1>
          <p className="text-xl text-gray-300">
            Every operation from folder creation to file listing
          </p>
        </motion.div>

        {/* Process Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => setActiveProcess('create')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeProcess === 'create'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            📁 Create Folder & Route
          </button>
          <button
            onClick={() => setActiveProcess('upload')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeProcess === 'upload'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            📤 Upload File
          </button>
          <button
            onClick={() => setActiveProcess('list')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeProcess === 'list'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            📋 List Files
          </button>
        </div>

        {/* Process Info */}
        <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 mb-6">
          <h2 className="text-2xl font-bold mb-2">{currentProcess.name}</h2>
          <p className="text-gray-300 mb-4">{currentProcess.description}</p>

          {activeProcess === 'create' && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Business Name:</label>
                  <div className="text-lg font-mono">{businessName}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Generated Slug:</label>
                  <div className="text-lg font-mono text-blue-400">/files/{slug}</div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => runSimulation(activeProcess)}
            disabled={runningSimulation}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition"
          >
            {runningSimulation ? 'Running Simulation...' : `Run ${currentProcess.name}`}
          </button>
        </div>

        {/* Steps Visualization */}
        <div className="space-y-4">
          {currentProcess.steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-gray-800/50 backdrop-blur rounded-xl p-6 border-2 transition-all ${
                step.status === 'active' ? 'border-blue-500 shadow-lg shadow-blue-500/20' :
                step.status === 'completed' ? 'border-green-500' :
                step.status === 'error' ? 'border-red-500' :
                'border-gray-700'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Step Number */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                  step.status === 'completed' ? 'bg-green-500 text-white' :
                  step.status === 'active' ? 'bg-blue-500 text-white animate-pulse' :
                  step.status === 'error' ? 'bg-red-500 text-white' :
                  'bg-gray-700 text-gray-400'
                }`}>
                  {step.status === 'completed' ? '✓' :
                   step.status === 'error' ? '✗' :
                   index + 1}
                </div>

                {/* Step Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                        {step.phase}
                      </div>
                      <h3 className="text-xl font-bold">{step.title}</h3>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Location:</div>
                      <div className="font-mono text-sm text-blue-400">{step.location}</div>
                      <div className="text-xs text-gray-500 mt-1">⏱ {step.duration}</div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="mt-3 space-y-1">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <span className="text-green-400">→</span>
                        {detail}
                      </div>
                    ))}
                  </div>

                  {/* Code Sample */}
                  {step.code && (
                    <AnimatePresence>
                      {(step.status === 'active' || step.status === 'completed') && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4"
                        >
                          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                            <code className="text-xs text-gray-300 font-mono">
                              {step.code}
                            </code>
                          </pre>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {/* Status Indicator */}
                  {step.status === 'active' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-3 flex items-center gap-2 text-blue-400"
                    >
                      <span className="animate-spin">⚙️</span>
                      <span className="text-sm font-semibold">Processing...</span>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-blue-400">
              {currentProcess.steps.length}
            </div>
            <div className="text-sm text-gray-400">Total Steps</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-400">
              {currentProcess.steps.filter(s => s.location.includes('D1')).length}
            </div>
            <div className="text-sm text-gray-400">Database Operations</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-purple-400">
              {currentProcess.steps.filter(s => s.location.includes('R2')).length}
            </div>
            <div className="text-sm text-gray-400">Storage Operations</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessFileSharingSteps;